import timezoneApi from '@/common/api/timezone.api';
import { useAuthStore } from '@/stores/auth';
import { zonedTimeToUtc } from 'date-fns-tz';

const TIMEZONE_SOURCE = Object.freeze({
    LOCATION: 'location',
    ORGANIZATION: 'organization',
    BROWSER: 'browser',
});

// A bare 'YYYY-MM-DD' calendar day: no time and no zone to reinterpret. Both the
// pass-through in toLocalServiceDate and the bound validation in facilityDayRangeToUtc
// ask this same question, so they share one definition of what a day string looks like.
const isBareDay = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

/**
 * Resolve the authoritative timezone for a claim's date of service, along with
 * which source it came from. Priority: facility Location.timeZone -> account
 * Organization.timeZone -> the browser's local zone.
 *
 * Account timezone is not set at account creation, so the browser fallback is a
 * real code path, not an edge case. The browser zone is used as the last resort
 * because a biller submitting claims is usually in the facility's timezone — a
 * far better guess than any hardcoded continental zone (a fixed zone would
 * systematically shift dates the wrong way for accounts elsewhere). This mirrors
 * the local-time precedent in useAppointmentFlow.js.
 *
 * This is the single source of truth for the precedence + fallback decision.
 * Both the claim-build flow (via resolveServiceTimeZone) and the report-level
 * timezone warning consume it, so the "is the browser fallback active?" rule
 * lives in exactly one place.
 *
 * @param {object|null} locationResource - The facility Location resource (or null).
 * @returns {{ timeZone: string, source: 'location'|'organization'|'browser', isFallback: boolean }}
 */
const resolveServiceTimeZoneInfo = (locationResource) => {
    const locationTimeZone = locationResource?.timeZone;
    if (locationTimeZone) {
        return { timeZone: locationTimeZone, source: TIMEZONE_SOURCE.LOCATION, isFallback: false };
    }
    const orgTimeZone = useAuthStore().organizationAccount?.timeZone;
    if (orgTimeZone) {
        return { timeZone: orgTimeZone, source: TIMEZONE_SOURCE.ORGANIZATION, isFallback: false };
    }
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return { timeZone: browserTimeZone, source: TIMEZONE_SOURCE.BROWSER, isFallback: true };
};

/**
 * Resolve the authoritative timezone identifier for a claim's date of service.
 * Thin accessor over resolveServiceTimeZoneInfo. Warns when both configured
 * sources are missing so the browser fallback is debuggable.
 *
 * @param {object|null} locationResource - The facility Location resource (or null).
 * @returns {string} An IANA timezone identifier.
 */
const resolveServiceTimeZone = (locationResource) => {
    const info = resolveServiceTimeZoneInfo(locationResource);
    if (info.source === TIMEZONE_SOURCE.BROWSER) {
        const orgId = useAuthStore().organizationAccount?.id ?? 'none';
        console.warn(`resolveServiceTimeZone: no timezone on Location (id=${locationResource?.id ?? 'none'}) or Organization (id=${orgId}); falling back to browser timezone ${info.timeZone}`);
    }
    return info.timeZone;
};

/**
 * Convert a UTC ISO instant to a plain 'YYYY-MM-DD' local date in the given
 * timezone. DST-aware (date-fns-tz under the hood). Falsy or '-' inputs pass
 * through unchanged. A bare 'YYYY-MM-DD' input also passes through unchanged: a
 * bare day has no time or zone to reinterpret and is already the correct
 * qualifying facility day, so converting it would spuriously shift it. On
 * conversion error, returns the original instant.
 *
 * @param {string} instant - A UTC ISO instant (e.g. '2026-06-10T03:30:00Z') or a bare 'YYYY-MM-DD'.
 * @param {string} tz - An IANA timezone identifier.
 * @returns {string} A 'YYYY-MM-DD' local date, or the input unchanged.
 */
const toLocalServiceDate = (instant, tz) => {
    if (!instant || instant === '-') return instant;
    if (isBareDay(instant)) return instant;
    try {
        return timezoneApi.getLocalDateTime(instant, 'yyyy-MM-dd', tz);
    } catch (error) {
        console.warn(`toLocalServiceDate: failed to convert "${instant}" in tz "${tz}":`, error?.message || error);
        return instant;
    }
};

/**
 * Convert a UTC/offset ISO instant to a local ISO datetime string *with offset*
 * ("yyyy-MM-ddTHH:mm:ssxxx") in the given timezone, preserving the time of day.
 *
 * Unlike toLocalServiceDate (date only), this keeps the clock time, so a
 * downstream consumer that has no timezone of its own — e.g. the CollaborateMD
 * mapper, which often receives no facility Location — still reads the correct
 * local hour straight off the offset. Falsy or '-' inputs pass through; on
 * conversion error the original instant is returned.
 *
 * @param {string} instant - A UTC/offset ISO instant (e.g. '2026-07-13T14:46:30Z').
 * @param {string} tz - An IANA timezone identifier.
 * @returns {string} A local ISO datetime with offset, or the input unchanged.
 */
const toLocalServiceDateTime = (instant, tz) => {
    if (!instant || instant === '-') return instant;
    try {
        return timezoneApi.getLocalDateTime(instant, "yyyy-MM-dd'T'HH:mm:ssxxx", tz);
    } catch (error) {
        console.warn(`toLocalServiceDateTime: failed to convert "${instant}" in tz "${tz}":`, error?.message || error);
        return instant;
    }
};

/**
 * Convert a facility-local day range ('YYYY-MM-DD' start and end) into the pair of
 * UTC instants that bracket it: the start day at 00:00:00.000 facility-local and the
 * end day at 23:59:59.999 facility-local.
 *
 * This is the inverse of toLocalServiceDate, and it exists because Encounters and
 * Appointments are stored as UTC instants while billing windows are facility-local
 * calendar days. Formatting a browser-local Date bound instead would move the window
 * with the viewer's timezone, and a bare day bound cannot match a facility evening
 * that has already crossed the UTC date line — 8pm in Denver is stored as the next
 * UTC day, so the final day of a window loses its evening rows.
 *
 * DST-aware: a spring-forward day spans 23 hours and a fall-back day 25. When no
 * timezone is supplied the browser zone is used, matching the last-resort fallback in
 * resolveServiceTimeZoneInfo.
 *
 * @param {string} startDayStr - The first facility-local day, 'YYYY-MM-DD'.
 * @param {string} endDayStr - The last facility-local day, 'YYYY-MM-DD'.
 * @param {string} tz - An IANA timezone identifier; falsy falls back to the browser zone.
 * @returns {{ start: string, end: string }|null} UTC ISO instants, or null when either day is missing or malformed.
 */
const facilityDayRangeToUtc = (startDayStr, endDayStr, tz) => {
    if (!isBareDay(startDayStr) || !isBareDay(endDayStr)) return null;
    const timeZone = tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return {
        start: zonedTimeToUtc(`${startDayStr}T00:00:00.000`, timeZone).toISOString(),
        end: zonedTimeToUtc(`${endDayStr}T23:59:59.999`, timeZone).toISOString(),
    };
};

/**
 * Localize a UTC instant to its facility-local 'YYYY-MM-DD' service date, resolving
 * the timezone from the charge's facility Location (looked up by id) via the shared
 * location -> organization -> browser precedence. This lets report data layers reuse
 * the same policy the claim build applies, so display, CSV, and day-bucketing all
 * agree with the submitted claim.
 *
 * Falsy / '-' / bare 'YYYY-MM-DD' inputs pass through unchanged (via toLocalServiceDate).
 *
 * @param {string} instant - A UTC ISO instant or a bare 'YYYY-MM-DD'.
 * @param {string|null} locationId - The facility Location id, or null when unknown.
 * @param {Array<object>} locations - Location resources to resolve the timezone from.
 * @returns {string} A facility-local 'YYYY-MM-DD', or the input unchanged.
 */
const localizeServiceDate = (instant, locationId, locations) => {
    const location = locationId ? (locations || []).find((l) => l.id === locationId) || null : null;
    const tz = resolveServiceTimeZoneInfo(location).timeZone;
    return toLocalServiceDate(instant, tz);
};

export { facilityDayRangeToUtc, localizeServiceDate, resolveServiceTimeZone, resolveServiceTimeZoneInfo, toLocalServiceDate, toLocalServiceDateTime };
